import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import CollapsiblePanel from './CollapsiblePanel';

const TAB_LABELS = {
  overview: '◈ Overview',
  transactions: '🧾 Transactions',
  subscriptions: '🔄 Subscriptions',
  magi: '🏥 MAGI',
  investments: '💎 Investments',
  ai: '🤖 AI',
  manage: '🛠 Manage',
  accounts: '🏦 Accounts',
};
const ALL_TABS = Object.keys(TAB_LABELS);

export default function HouseholdMembers({ user }) {
  const [members, setMembers] = useState(null);
  const [saving, setSaving] = useState(null);

  const load = useCallback(async () => {
    try {
      const data = await api.getHouseholdMembers();
      setMembers(data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Only the owner can manage this, and only if there's more than one member
  if (user.role !== 'owner') return null;
  if (!members || members.length < 2) return null;

  const toggleTab = async (member, tabId) => {
    const current = member.visible_tabs || ALL_TABS; // null means "all visible"
    const next = current.includes(tabId)
      ? current.filter((t) => t !== tabId)
      : [...current, tabId];

    setSaving(member.id);
    try {
      await api.updateMemberVisibleTabs(member.id, next);
      load();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(null);
    }
  };

  const resetToAll = async (member) => {
    setSaving(member.id);
    try {
      await api.updateMemberVisibleTabs(member.id, null);
      load();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(null);
    }
  };

  return (
    <CollapsiblePanel title="👥 Household Members & Access">
      <p style={styles.helpText}>
        Control which tabs each co-manager can see. This hides tabs from
        their navigation menu - it's a convenience setting, not a strict
        security boundary.
      </p>

      {members.filter((m) => m.role !== 'owner').map((member) => {
        const visible = member.visible_tabs || ALL_TABS;
        const isRestricted = member.visible_tabs !== null;

        return (
          <div key={member.id} style={styles.memberBlock}>
            <div style={styles.memberHeader}>
              <div>
                <div style={styles.memberName}>{member.name}</div>
                <div style={styles.memberEmail}>{member.email}</div>
              </div>
              {isRestricted && (
                <button
                  style={styles.resetBtn}
                  onClick={() => resetToAll(member)}
                  disabled={saving === member.id}
                >
                  Reset to full access
                </button>
              )}
            </div>
            <div style={styles.tabGrid}>
              {ALL_TABS.map((tabId) => (
                <label key={tabId} style={styles.tabCheck}>
                  <input
                    type="checkbox"
                    checked={visible.includes(tabId)}
                    onChange={() => toggleTab(member, tabId)}
                    disabled={saving === member.id}
                  />
                  {TAB_LABELS[tabId]}
                </label>
              ))}
            </div>
          </div>
        );
      })}
    </CollapsiblePanel>
  );
}

const styles = {
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
  },
  title: {
    margin: '0 0 0.5rem',
    fontSize: '1rem',
    fontWeight: '700',
    color: '#fff',
    fontFamily: "'Syne', sans-serif",
  },
  helpText: {
    color: '#6b7280',
    fontSize: '0.8rem',
    lineHeight: '1.5',
    marginBottom: '1.25rem',
  },
  memberBlock: {
    background: '#0a0f1e',
    border: '1px solid #1e2a3a',
    borderRadius: '10px',
    padding: '1rem',
    marginBottom: '0.75rem',
  },
  memberHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.75rem',
  },
  memberName: {
    color: '#e5e7eb',
    fontSize: '0.9rem',
    fontWeight: '600',
    fontFamily: "'Syne', sans-serif",
  },
  memberEmail: {
    color: '#4b5563',
    fontSize: '0.7rem',
    fontFamily: "'DM Mono', monospace",
  },
  resetBtn: {
    background: 'transparent',
    color: '#00d4aa',
    border: '1px solid #00d4aa',
    borderRadius: '6px',
    padding: '4px 10px',
    fontSize: '0.7rem',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: "'Syne', sans-serif",
  },
  tabGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.5rem',
  },
  tabCheck: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    color: '#9ca3af',
    fontSize: '0.8rem',
    fontFamily: "'Syne', sans-serif",
  },
};
